<template>
  <div class="sdi-strategies-container">
    <div class="sdi-strategies">
      <p class="instructions">Recommend UDL-aligned instructional strategies for implementing this goal based on the following information about my student:</p>
      <div aria-live="polite">
        <SDIStrategiesResults
          v-if="generatedStrategies"
          :generated-strategies="generatedStrategies"
          :selected-characteristics="selectedCharacteristics"
          @reset="handleReset"
          :trace-i-d="traceID"
        />
      </div>
      <CharacteristicsForm
        v-if="!generatedStrategies"
        :characteristic-options="characteristicOptions"
        :selected-characteristics="selectedCharacteristics"
        @updatedSelectedCharacteristics="setSelectedCharacteristics"
        @submit="handleSubmit"
        @clearSelections="clearSelections"
      />
      <AIDisclaimer />
    </div>
    <GeneratingMessage
      v-if="generating || generationError"
      :generation-error="generationError"
      @retry="handleSubmit"
      @cancel="generationError = ''"
    />
  </div>
</template>

<script>
import { nanoid } from 'nanoid'
import ChipsInput from '@/components/ChipsInput.vue'
import { getSdiStategies, getSdiStudentCharacteristics } from '@/api/pathways'
import SDIStrategiesResults from '@/components/sdistrategies/SDIStrategiesResults.vue'
import GeneratingMessage from '@/components/sdistrategies/GeneratingMessage.vue'
import CharacteristicsForm from '@/components/sdistrategies/CharacteristicsForm.vue'
import AIDisclaimer from '@/components/AIDisclaimer.vue'
export default {
  name: 'SDIStrategies',
  components: { AIDisclaimer, CharacteristicsForm, GeneratingMessage, SDIStrategiesResults, ChipsInput },
  props: {
    goalText: {
      type: String,
      required: true,
      default: '',
    },
    goalGrades: {
      type: Array,
      required: true,
      default() {
        return []
      },
    },
  },
  data() {
    return {
      selectedCharacteristics: [],
      characteristics: [],
      generating: false,
      generatedStrategies: null,
      generationError: '',
      traceID: '',
    }
  },
  computed: {
    characteristicOptions() {
      return this.characteristics || []
    },
    formattedGradeLevel() {
      if (!this.goalGrades) {
        return ''
      }
      const chosenGrade = this.goalGrades[0]
      switch (chosenGrade) {
        case 'Birth-3':
          return 'Birth - 3 years old'
        case 'Pre-K':
          return 'Pre-K'
        case 'K':
          return 'Kindergarten'
        case '1':
          return '1st grade'
        case '2':
          return '2nd grade'
        case '3':
          return '3rd grade'
        case '4':
        case '5':
        case '6':
        case '7':
        case '8':
        case '9':
        case '10':
        case '11':
        case '12':
          return `${chosenGrade}th grade`
      }
    },
    selectedCharacteristicsNames() {
      if (!this.selectedCharacteristics || !this.selectedCharacteristics.length) {
        return ''
      }
      return this.selectedCharacteristics.map(c => c.name)
    }
  },
  methods: {
    setSelectedCharacteristics(selectedCharacteristics) {
      this.selectedCharacteristics = selectedCharacteristics
    },
    async fetchCharacteristics() {
      const { characteristics } = await getSdiStudentCharacteristics()
      this.characteristics = characteristics
        .map(characteristic => {
          characteristic.area_uuid = characteristic.id
          characteristic.area_label = characteristic.name
          return characteristic
        })
    },
    async handleSubmit() {
      this.generatedStrategies = null
      this.generationError = ''
      const characteristics = this.selectedCharacteristics.map(characteristic => ({
        name: characteristic.name,
        id: characteristic.id,
      }))
      const trace = nanoid()
      this.traceID = trace
      try {
        this.generating = true
        this.$sendEvent('Clicked Generate SDI Strategies', {sdi_trace: trace})
        const generatedStrategies = await getSdiStategies({
          goal_text: this.goalText,
          grade_level: this.formattedGradeLevel,
          characteristics: characteristics,
          sdi_trace: trace,
          anchor_page_url: window.location.href
        })
        this.generating = false
        this.validateStrategies(generatedStrategies)
        this.generatedStrategies = generatedStrategies
      } catch (error) {
        this.generating = false
        this.generationError = 'There was an error generating your strategies.'
        console.error('Error generating SDI strategies: ', error)
      }
    },
    validateStrategies(strategies) {
      if (
        !strategies ||
        !strategies.engagement ||
        !strategies.expression ||
        !strategies.representation
      ) {
        throw new Error('invalid strategy response')
      }
    },
    handleReset() {
      this.generatedStrategies = null
      this.$sendEvent('Clicked Start Over SDI Strategies', {
        sdi_characteristics: this.selectedCharacteristicsNames
      })
      this.clearSelections(false)
    },
    clearSelections(sendEvent = true) {
      if (sendEvent) {
        this.$sendEvent('Clicked Cleared SDI Strategies', {
          sdi_characteristics: this.selectedCharacteristicsNames
        })
      }
      this.setSelectedCharacteristics([])
    },
  },
  eventContext() {
    return {
      sdi_characteristics: this.selectedCharacteristicsNames
    }
  },
  mounted() {
    this.fetchCharacteristics()
  },
}
</script>

<style scoped lang="less">
.sdi-strategies-container {
  position: relative;
  font-size: 16px;

  &.acc-objectives-indent {
    .sdi-strategies {
      padding: 0 48px 16px;
    }
  }

  .instructions {
    margin-bottom: 1rem;
  }
}
</style>
